const localStorageSlim = require('localstorage-slim');
const BrowserInfo = require('public/js/lib/browser-info');
const cacheConfig = require('public/js/lib/cache-config');
const { applySelectedDealerToDom } = require('public/js/lib/dealer-display');
const { LOCATION_CHANGED_EVENT } = require('public/js/lib/location-events');
const LOCATION_SOURCE = require('public/js/lib/location-source');

/**
 * @param {object} params.window - DOM window object
 * @returns {{ setBrowserLocation:function }}
 */
module.exports = ({ window }) => {
	const { document } = window;

	/**
	 * After Geo-IP succeeds we still attempt browser geolocation; if that fails we must not wipe
	 * the header — terminal is only for "resolution ended with nothing to show".
	 *
	 * @returns {boolean}
	 */
	const hasPersistedBuyerLocationCache = () => {
		try {
			const cached = localStorageSlim.get(cacheConfig.LOCATION_CACHE_KEY);
			const postalCode = String(cached?.postalCode ?? '').trim();

			return Boolean(cached?.location) && postalCode !== '';
		} catch {
			return false;
		}
	};

	const broadcastLocationResolutionTerminal = () => {
		if (hasPersistedBuyerLocationCache()) {
			return;
		}
		rp$(document).trigger(LOCATION_CHANGED_EVENT, [{
			hasResolvedLocation: false,
			location: null,
			postalCode: '',
			resolutionTerminal: true,
			source: null,
		}]);
	};

	/**
	 * Owner of the cache+broadcast contract: write `LOCATION_CACHE_KEY` then emit
	 * `LOCATION_CHANGED_EVENT` so subscribers (header, PDP, find-dealer) repaint
	 * from a single signal.
	 *
	 * @param {object} location  Raw IPLocation values from the server.
	 * @param {string} source    One of LOCATION_SOURCE.*
	 * @returns {boolean}        True when a postal was present and the event was emitted.
	 */
	const persistAndBroadcastLocation = (location, source) => {
		const postalCode = String(location?.postal_code ?? '').trim();
		if (postalCode === '') {
			return false;
		}
		try {
			localStorageSlim.set(
				cacheConfig.LOCATION_CACHE_KEY,
				{ postalCode, location, source },
				{ ttl: cacheConfig.getTtlFor('location') },
			);
		} catch {
			// no-op: storage unavailable; broadcast still proceeds so live UI updates
		}
		rp$(document).trigger(LOCATION_CHANGED_EVENT, [{ postalCode, location, source }]);

		return true;
	};

	// Match server-side `number_format($lat, 5)` precision and absorb sub-meter GPS fluctuations.
	const normalizeCoordinate = (value) => {
		const parsed = Number(value);
		if (!Number.isFinite(parsed)) {
			return null;
		}

		return Math.round(parsed * 1e5) / 1e5;
	};

	const getCachedFailedCoords = () => {
		try {
			const cached = localStorageSlim.get(cacheConfig.LOCATION_FAILED_COORDS_CACHE_KEY);
			if (!Number.isFinite(cached?.latitude) || !Number.isFinite(cached?.longitude)) {
				return null;
			}

			return { latitude: cached.latitude, longitude: cached.longitude };
		} catch {
			return null;
		}
	};

	const cacheFailedCoords = (latitude, longitude) => {
		try {
			localStorageSlim.set(
				cacheConfig.LOCATION_FAILED_COORDS_CACHE_KEY,
				{ latitude, longitude },
				{ ttl: cacheConfig.getTtlFor('locationFail') },
			);
		} catch {
			// no-op: storage unavailable
		}
	};

	const clearCachedFailedCoords = () => {
		try {
			localStorageSlim.remove(cacheConfig.LOCATION_FAILED_COORDS_CACHE_KEY);
		} catch {
			// no-op: storage unavailable
		}
	};

	const matchesCachedFailedCoords = (latitude, longitude) => {
		const cached = getCachedFailedCoords();
		if (!cached) {
			return false;
		}

		return cached.latitude === latitude && cached.longitude === longitude;
	};

	// Suppress only when the server has explicitly rejected the coords (4xx). Network failures
	// (status 0: offline, abort, DNS, CORS) and 5xx outages are transient — let the next session retry.
	const isServerRejection = (jqXHR) => {
		const status = Number(jqXHR?.status);
		return Number.isFinite(status) && status >= 400 && status < 500;
	};

	const postBrowserLocation = (location) => {
		const latitude = normalizeCoordinate(location?.latitude);
		const longitude = normalizeCoordinate(location?.longitude);

		if (latitude === null || longitude === null) {
			broadcastLocationResolutionTerminal();
			return;
		}

		if (matchesCachedFailedCoords(latitude, longitude)) {
			broadcastLocationResolutionTerminal();
			return;
		}

		// `LocationController::setCachedIPLocation` contract:
		//   200 + location.postal_code present  → resolved (clear suppression)
		//   200 + location.postal_code missing  → no zip_code_mapping row within ±2° of these coords
		//                                          (persistent data gap; suppress for TTL)
		//   422                                  → matched mapping row failed validation (persistent data
		//                                          quality issue; suppressed via isServerRejection 4xx filter)
		//   5xx / status 0                       → transient (server outage / offline); not cached
		$ajax({
			method: 'POST',
			dataType: 'json',
			url: '/ajax/ip-location/update',
			data: { location },
			success: (response) => {
				const resolvedBuyerLocation = response?.location;
				if (resolvedBuyerLocation?.postal_code) {
					clearCachedFailedCoords();
					applySelectedDealerToDom(response?.selected_dealer ?? null);
					persistAndBroadcastLocation(resolvedBuyerLocation, LOCATION_SOURCE.BROWSER);
					return;
				}
				cacheFailedCoords(latitude, longitude);
				broadcastLocationResolutionTerminal();
			},
			error: (jqXHR) => {
				if (isServerRejection(jqXHR)) {
					cacheFailedCoords(latitude, longitude);
				}
				broadcastLocationResolutionTerminal();
			},
		});
	};

	return {
		/* Set browser location (contains lat/long only) if ip-location not cached [Currently Lead-Gen Only]
		.*-----------------------------------------*/
		setBrowserLocation() {
			$ajax({
				dataType: 'json',
				url: '/ajax/ip-location/get',
				error: () => {
					broadcastLocationResolutionTerminal();
				},
				success: (response) => {
					const hasBrowserTier = response?.has_browser_tier === true;
					const hasManualLocation = response?.source === LOCATION_SOURCE.MANUAL;
					const isLeadGen = response?.is_lead_gen === true;
					const resolvedBuyerLocation = response?.location ?? null;

					applySelectedDealerToDom(response?.selected_dealer ?? null);
					let didPersistBroadcast = false;
					if (resolvedBuyerLocation) {
						didPersistBroadcast = persistAndBroadcastLocation(
							resolvedBuyerLocation,
							response?.source,
						);
					}

					if (hasBrowserTier || hasManualLocation) {
						if (!resolvedBuyerLocation || !didPersistBroadcast) {
							broadcastLocationResolutionTerminal();
						}
						return;
					}

					// Browser geolocation (and its permission prompt) is lead-gen only.
					if (!isLeadGen) {
						if (!didPersistBroadcast) {
							broadcastLocationResolutionTerminal();
						}
						return;
					}

					const browserInfo = BrowserInfo({ window });
					browserInfo.fetchGeolocation(
						(location) => {
							if (location) {
								postBrowserLocation(location);
								return;
							}
							broadcastLocationResolutionTerminal();
						},
						() => {
							broadcastLocationResolutionTerminal();
						}
					);
				},
			});
		},
	};
};
